import { LABELED_CHIP_ICON_INLINE_START_SPACING } from '@components/v2/ui/LabeledChip/labeledChip.styles';
import { Theme, useTheme } from '@mui/material';
import { useCallback, useRef } from 'react';
import {
  AdjustLastVisibleTagIfNeededProps,
  CalculateVisibleAndHiddenTagsParams,
  MeasureTagWidthParams,
  TagDynamicProps,
  VisibleTag,
} from '../DynamicTags.types';

const CHIP_LABEL_PADDING_INLINE = 0.5;
const FALLBACK_CHAR_WIDTH = 6.5;

const measurementCache = new Map<string, number>();

let measurementContext: CanvasRenderingContext2D | null = null;

const getMeasurementContext = () => {
  if (!measurementContext) {
    measurementContext = document.createElement('canvas').getContext('2d');
  }

  return measurementContext;
};

document.fonts?.ready.then(() => {
  measurementCache.clear();
});

const getChipLabelFont = (theme: Theme) => {
  const { fontWeight, fontSize } = theme.typography.body2;

  return `${fontWeight} ${fontSize} ${theme.typography.fontFamily}`;
};

const getChipChromeWidth = (theme: Theme, hasIcon: boolean) => {
  const labelPadding = 2 * parseFloat(theme.spacing(CHIP_LABEL_PADDING_INLINE));
  const iconMargin = hasIcon
    ? parseFloat(theme.spacing(LABELED_CHIP_ICON_INLINE_START_SPACING))
    : 0;

  return labelPadding + iconMargin;
};

const measureTextContentWidth = (text: string, font: string) => {
  const context = getMeasurementContext();

  if (!context) {
    return text.length * FALLBACK_CHAR_WIDTH;
  }

  context.font = font;

  return context.measureText(text).width;
};

const measureTagWidth = ({ theme, text, iconWidth = 0 }: MeasureTagWidthParams) => {
  const font = getChipLabelFont(theme);
  const cacheKey = `${font}:${text}`;
  const textWidth = measurementCache.get(cacheKey) ?? measureTextContentWidth(text, font);

  measurementCache.set(cacheKey, textWidth);

  return textWidth + getChipChromeWidth(theme, iconWidth > 0) + iconWidth;
};

const adjustLastVisibleTagIfNeeded = ({
  hiddenTags,
  overflowWidth,
  overflowWidthWithGapAndPadding,
  spaceLeft,
  visibleTags,
}: AdjustLastVisibleTagIfNeededProps) => {
  const lastIndex = visibleTags.length - 1;
  if (lastIndex < 0) {
    return;
  }
  const lastVisibleTag = visibleTags[lastIndex];
  const adjustedWidth = lastVisibleTag.maxWidth - overflowWidth + spaceLeft;
  if (adjustedWidth >= overflowWidthWithGapAndPadding) {
    lastVisibleTag.maxWidth = adjustedWidth;
    lastVisibleTag.hasEllipsis = true;
  } else {
    const removed = visibleTags.pop();
    if (removed) {
      hiddenTags.push(removed);
    }
  }
};

const calculateVisibleAndHiddenTags = ({
  theme,
  tags,
  containerWidth,
  tagGap,
  overflowWidth,
  overflowWidthWithGapAndPadding,
}: CalculateVisibleAndHiddenTagsParams): {
  visibleTags: VisibleTag[];
  hiddenTags: TagDynamicProps[];
} => {
  const finalLayout = tags.reduce(
    (acc, tag) => {
      if (acc.stopProcessing) {
        return acc;
      }
      const tagNaturalWidth = measureTagWidth({
        theme,
        text: tag.label,
        iconWidth: tag.iconProps?.width,
      });
      const spaceLeft = containerWidth - acc.totalUsedWidth;
      if (spaceLeft > overflowWidthWithGapAndPadding) {
        const maxWidth = Math.min(tagNaturalWidth, spaceLeft);
        const hasEllipsis = maxWidth !== tagNaturalWidth;
        acc.newVisibleTags.push({ ...tag, maxWidth, hasEllipsis });
        acc.totalUsedWidth += maxWidth + tagGap;
        acc.newHiddenTags.shift();
      } else {
        adjustLastVisibleTagIfNeeded({
          visibleTags: acc.newVisibleTags,
          hiddenTags: acc.newHiddenTags,
          spaceLeft,
          overflowWidth,
          overflowWidthWithGapAndPadding,
        });
        acc.stopProcessing = true;
      }

      return acc;
    },
    {
      newVisibleTags: [] as VisibleTag[],
      newHiddenTags: [...tags],
      totalUsedWidth: 0,
      stopProcessing: false,
    },
  );
  return {
    visibleTags: finalLayout.newVisibleTags,
    hiddenTags: finalLayout.newHiddenTags,
  };
};

export const useMeasureTextWidthToTag = () => {
  const theme = useTheme();
  const boxDynamicContainerRef = useRef<HTMLDivElement>(null);

  const calculateLayoutAndReturnVisibleAndHiddenTags = useCallback(
    (tags: TagDynamicProps[], tagGap: number) => {
      if (!boxDynamicContainerRef.current || tags.length === 0) {
        return {
          visibleTags: [],
          hiddenTags: [],
        };
      }

      const containerWidth = boxDynamicContainerRef.current.getBoundingClientRect().width;

      if (containerWidth === 0) {
        return null;
      }

      const overflowWidth = measureTagWidth({ theme, text: `+${tags.length}` });
      const overflowWidthWithGapAndPadding = overflowWidth + tagGap;
      return calculateVisibleAndHiddenTags({
        theme,
        tags,
        containerWidth,
        tagGap,
        overflowWidth,
        overflowWidthWithGapAndPadding,
      });
    },
    [theme],
  );

  return {
    boxDynamicContainerRef,
    calculateLayoutAndReturnVisibleAndHiddenTags,
  };
};
